import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ApiCall from "../utils/apicall";
import { showSuccessToast, showErrorToast } from '../utils/Toast';

export const getContacts = createAsyncThunk(
  "getContacts/contacts",
  async (ThunkApi) => {
    try {
      const res = await ApiCall.get("/contact");
      return res.data.data.contacts;
    } catch (error) {
      return ThunkApi.rejectWithValue(error.response.data.message);
    }
  }
);

export const createContact = createAsyncThunk(
  "createContact/contacts",
  async (data, ThunkApi) => {
    try {
      const res = await ApiCall.post("/contact", data);
      //  console.log(res);
      return res.data;
    } catch (error) {
      const { message } = error.response.data;
      throw new Error(message);
    }
  }
);

export const updateContact = createAsyncThunk(
  "updateContacts/contacts",
  async (data, ThunkApi) => {
    try {
      const res = await ApiCall.put(`/contact/${data.id}`, data);
      console.log(res);

      return res.data;
    } catch (error) {
      const { message } = error.response.data;
      throw new Error(message);
    }
  }
);

export const deleteContact = createAsyncThunk(
  "deleteContact/contacts",
  async (data, ThunkApi) => {
    try {
      const res = await ApiCall.delete(`/contact/${data}`);
      if (res.data?.success) {
        return data;
      }
      // Codex WARNING fix: explicit reject on success=false so UI doesn't
      // pretend the contact was deleted.
      return ThunkApi.rejectWithValue(res.data?.message || "Server reported failure");
    } catch (error) {
      return ThunkApi.rejectWithValue(
        error?.response?.data?.message || error?.message || "Failed to delete contact"
      );
    }
  }
);

export const importContacts = createAsyncThunk(
  "ImportContacts/contacts",
  async (data, ThunkApi) => {
    try {
      const res = await ApiCall.post("/contact/import", data, {
        headers: {
          "Content-Type": "application/json", // Set the content type to indicate JSON data
        },
      });
      // Sanity-check the server-side success flag before fulfilling.
      if (res.data && res.data.success === false) {
        return ThunkApi.rejectWithValue(res.data.message || "Import failed");
      }
      return res.data.data;
    } catch (error) {
      // Codex WARNING fix: previously `return error` here, which fulfilled
      // the action with the error object as payload — UI then toast-success'd
      // a failed import. Reject properly.
      return ThunkApi.rejectWithValue(
        error?.response?.data?.message || error?.message || "Contact import failed"
      );
    }
  }
);

export const ContactSlice = createSlice({
  name: "contacts",
  initialState: {
    contacts: [],
    isContactLoading: false,
    isSuccess: false,
  },
  extraReducers: (builder) => {
    builder.addCase(getContacts.pending, (state, action) => {
      state.isContactLoading = true;
      state.isSuccess = false;
    });
    builder.addCase(getContacts.fulfilled, (state, action) => {
      // Codex WARNING fix: previously isSuccess=false on fulfilled, leaving
      // consumers unable to tell load-completed-successfully from load-failed.
      state.contacts = action.payload;
      state.isContactLoading = false;
      state.isSuccess = true;
    });
    builder.addCase(getContacts.rejected, (state, action) => {
      state.isContactLoading = false;
      state.isSuccess = false;
          showErrorToast('Failed to load contacts. Please refresh.');
    });
    builder.addCase(deleteContact.pending, (state, action) => {
      // console.log('in extra pending')
      state.isContactLoading = true;
    });
    builder.addCase(deleteContact.fulfilled, (state, action) => {
      state.contacts = state.contacts.filter(
        (contacts) => contacts.id !== action.payload
      );
      state.isContactLoading = false;
          showSuccessToast('Contact deleted successfully.');
    });
    builder.addCase(deleteContact.rejected, (state, action) => {
      state.isContactLoading = false;
          showErrorToast('Failed to delete contact. Please try again.');
    });

    builder.addCase(createContact.pending, (state, action) => {
      // console.log('in extra add pending')
      state.isContactLoading = true;
    });
    builder.addCase(createContact.fulfilled, (state, action) => {
      state.contacts = [...state.contacts, action.payload];
      state.isContactLoading = false;
          showSuccessToast('Contact created successfully.');
    });
    builder.addCase(createContact.rejected, (state, action) => {
      state.isContactLoading = false;
          showErrorToast('Failed to create contact. Please try again.');
      return action.payload;
    });

    builder.addCase(updateContact.pending, (state, action) => {
      // console.log('in extra update pending')
      state.isContactLoading = true;
    });
    builder.addCase(updateContact.fulfilled, (state, action) => {
      const updatedContact = action.payload;
      state.contacts = state.contacts.map((contact) => {
        if (contact.id === updatedContact.id) {
          return updatedContact;
        }
        return contact;
      });
      state.isContactLoading = false;
          showSuccessToast('Contact updated successfully.');
    });
    builder.addCase(updateContact.rejected, (state, action) => {
      state.isContactLoading = false;
          showErrorToast('Failed to update contact. Please try again.');
      return action.payload;
    });

    builder.addCase(importContacts.pending, (state, action) => {
      // console.log('in extra add pending')
      state.isContactLoading = true;
    });
    builder.addCase(importContacts.fulfilled, (state, action) => {
      state.contacts = [...action.payload, ...state.contacts];
      state.isContactLoading = false;
          showSuccessToast('Contacts imported successfully.');
    });
    builder.addCase(importContacts.rejected, (state, action) => {
      state.isContactLoading = false;
    });
  },
});

export default ContactSlice.reducer;
